import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import {
  Typography, Select, DatePicker, Button, Space, Spin, Empty,
  Popover, Tooltip, message, Modal, Alert, Segmented, Checkbox,
} from 'antd';
import { ClockCircleOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { slotMins, classifyState, staffingPerSlot, absenceShare } from '../utils/staffingCounts';
import { UserContext } from '../App';
import SchedulePersonDrawer from '../components/SchedulePersonDrawer';

// ── constants ──────────────────────────────────────────────────────────────
// QA Coaching and Non-ACD: time off the queue that is not a break. In the
// Actual view QA Coaching is its CCaaS state, and Non-ACD any other state
// not a break, a lunch or Active 4 SLA (routes/schedule.py).
const SLOT_META = {
  Break:     { label: 'B', bg: '#fa8c16', fg: '#fff' },
  Lunch:     { label: 'L', bg: '#389e0d', fg: '#fff' },
  'QA Coaching': { label: 'C', bg: '#722ed1', fg: '#fff' },
  'Non-ACD': { label: 'N', bg: '#08979c', fg: '#fff' },
  Other:     { label: '?', bg: '#8c8c8c', fg: '#fff' },
};
const PLAN_TYPES = ['Break', 'Lunch', 'QA Coaching', 'Non-ACD'];
const OFFQ_TYPES = ['QA Coaching', 'Non-ACD'];

const SHIFT_BG = '#dbeafe';

// Absences over the schedule: striped red, with the reason's letter.
const ABSENT_FG = '#a8071a';
const absentBg = (base) =>
  `repeating-linear-gradient(135deg, #ffccc7 0 5px, ${base} 5px 10px)`;
const REASON_LETTER = {
  'Sick/Health Related': 'S', 'Jury Duty': 'J', 'Bereavement': 'B', 'Outage': 'O', 'Other': 'X',
};

const EMPLOYEE_COL_W = 280;
const JOB_COL_W      = 120;
const SLOT_COL_W     = 54;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i < 12 ? 'AM' : 'PM';
  const h12  = i === 0 ? 12 : i > 12 ? i - 12 : i;
  return { label: `${h12}:00 ${ampm}`, value: i };
});

// ── helpers ────────────────────────────────────────────────────────────────
function generateTimeSlots(startHour, endHour) {
  const slots = [];
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
}

// Full time for every column, e.g. 7:15am.
function slotLabel(slot) {
  const h = parseInt(slot.slice(0, 2), 10);
  const m = slot.slice(3);
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m}${h < 12 ? 'am' : 'pm'}`;
}


// ── cell styles ─────────────────────────────────────────────────────────────
const agentHeaderStyle = {
  position: 'sticky', left: 0, top: 0, zIndex: 5,
  background: '#f0f0f0',
  padding: '6px 10px',
  textAlign: 'left',
  fontSize: 12, fontWeight: 600,
  borderBottom: '2px solid #d9d9d9',
  borderRight: '1px solid #e0e0e0',
  width: EMPLOYEE_COL_W, minWidth: EMPLOYEE_COL_W,
  whiteSpace: 'nowrap',
};

const jobHeaderStyle = {
  position: 'sticky', left: EMPLOYEE_COL_W, top: 0, zIndex: 5,
  background: '#f0f0f0',
  padding: '6px 10px',
  textAlign: 'left',
  fontSize: 12, fontWeight: 600,
  borderBottom: '2px solid #d9d9d9',
  borderRight: '2px solid #bfbfbf',
  width: JOB_COL_W, minWidth: JOB_COL_W,
  whiteSpace: 'nowrap',
};

const agentCellStyle = (bg) => ({
  position: 'sticky', left: 0, zIndex: 1,
  background: bg,
  padding: '2px 10px',
  fontSize: 12,
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #e0e0e0',
  width: EMPLOYEE_COL_W, minWidth: EMPLOYEE_COL_W, maxWidth: EMPLOYEE_COL_W,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
});

const jobCellStyle = (bg) => ({
  position: 'sticky', left: EMPLOYEE_COL_W, zIndex: 1,
  background: bg,
  padding: '2px 10px',
  fontSize: 12,
  borderBottom: '1px solid #f0f0f0',
  borderRight: '2px solid #bfbfbf',
  width: JOB_COL_W, minWidth: JOB_COL_W, maxWidth: JOB_COL_W,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  color: '#595959',
});

// ── component ──────────────────────────────────────────────────────────────
// embedded (Absence Notifications > By Hour): read-only, no heading or Copy,
// absences shown; teams start from initialTeamIds, and lockTeams hides the
// team picker (Campaign Summary's team).
export default function SchedulePage({ embedded = false, initialTeamIds = null, lockTeams = false }) {
  const currentUser = useContext(UserContext);
  const canEdit   = !embedded && (currentUser?.CanEditAbsences ?? 0) >= 2;
  // Absences over the schedule, and the counts under each team: for anyone
  // with Absences access (never agents, who would see co-workers' reasons).
  const canSeeAbsences = currentUser?.Role !== 'agent' && (currentUser?.CanEditAbsences ?? 0) >= 1;
  // Who could cover a scheduled person's shift: the WFM Shift Swap Board's
  // people (admins and Absences access 3 and up).
  const canBoard = currentUser?.Role === 'admin' || (currentUser?.CanEditAbsences ?? 0) >= 3;
  // A person's WFM Availability and WFM Skills: for those who can view WFM
  // Skills (WFM Availability's own rule).
  const canWfm = currentUser?.Role === 'admin' || (currentUser?.CanEditWFMSkills ?? 0) >= 1;
  // The person whose panel is open (name click).
  const [personFor, setPersonFor] = useState(null);

  const [allTeams,      setAllTeams]      = useState([]);
  const [teamIds,       setTeamIds]       = useState(() => (initialTeamIds?.length ? initialTeamIds : []));
  const [allJobs,       setAllJobs]       = useState([]);
  const [jobIds,        setJobIds]        = useState([]);
  const [date,          setDate]          = useState(dayjs());
  const [startHour,     setStartHour]     = useState(7);
  const [endHour,       setEndHour]       = useState(19);
  const [view,          setView]          = useState('plan');
  const [excludeUnscheduled,   setExcludeUnscheduled]   = useState(true);
  const [hideShortBreakLunch, setHideShortBreakLunch] = useState(true);
  const [showAbsences,  setShowAbsences]  = useState(true);
  const [absenceMap,    setAbsenceMap]    = useState({}); // {agentId: [absence with Spans]}

  // Plan view state
  const [agents,        setAgents]        = useState([]);
  const [slotMap,       setSlotMap]       = useState({});
  const [shiftMap,      setShiftMap]      = useState({}); // {agentId: [{start,end}]}
  const [loading,       setLoading]       = useState(false);
  const [openCell,      setOpenCell]      = useState(null);
  const [saving,        setSaving]        = useState(new Set());

  // Actual view state
  const [actualSlotMap, setActualSlotMap] = useState({});
  const [actualLoading, setActualLoading] = useState(false);

  // Copy modal state
  const [copyOpen,  setCopyOpen]  = useState(false);
  const [copyNonAcd, setCopyNonAcd] = useState(false);   // QA Coaching is never copied
  const [copyDate,  setCopyDate]  = useState(null);
  const [copying,   setCopying]   = useState(false);

  const containerRef = useRef(null);
  const timeSlots = generateTimeSlots(startHour, endHour);

  // Load team and job lists
  useEffect(() => {
    api.get('/api/teams').then(res => setAllTeams(res.data)).catch(() => {});
    api.get('/api/jobs').then(res => setAllJobs(res.data || [])).catch(() => {});
  }, []);

  const teamOptions = (() => {
    const vis = currentUser?.VisibleTeams || [];
    if (vis.length) return vis;
    if (currentUser?.Role === 'agent') {
      const myTeam = allTeams.find(t => t.ID === currentUser.TeamID);
      return myTeam ? [myTeam] : [];
    }
    return allTeams;
  })();

  const initialKey = (initialTeamIds || []).join(',');
  useEffect(() => {
    if (embedded && initialTeamIds?.length) setTeamIds(initialTeamIds);
  }, [embedded, initialKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default to the current user's own team; fall back to all visible if unassigned
  useEffect(() => {
    if (teamIds.length === 0 && teamOptions.length && currentUser) {
      const myTeamId = currentUser.TeamID;
      if (myTeamId && teamOptions.some(t => t.ID === myTeamId)) {
        setTeamIds([myTeamId]);
      } else {
        setTeamIds(teamOptions.map(t => t.ID));
      }
    }
  }, [teamOptions, teamIds.length, currentUser]);


  // ── Plan data ────────────────────────────────────────────────────────────
  const loadPlan = useCallback(async () => {
    if (!teamIds.length || !date) { setAgents([]); setSlotMap({}); setShiftMap({}); return; }
    setLoading(true);
    try {
      const baseParams = { team_ids: teamIds.join(','), date: date.format('YYYY-MM-DD') };
      const planParams = { ...baseParams };
      if (jobIds.length) planParams.job_ids = jobIds.join(',');
      const [planRes, shiftRes] = await Promise.all([
        api.get('/api/schedule',        { params: planParams }),
        api.get('/api/schedule/shifts', { params: baseParams }),
      ]);
      setAgents(planRes.data.agents || []);
      const slotM = {};
      for (const s of planRes.data.slots || []) {
        slotM[`${s.AgentUserID}-${s.SlotTime}`] = { ID: s.ID, SlotType: s.SlotType };
      }
      setSlotMap(slotM);
      const shiftM = {};
      for (const s of shiftRes.data || []) {
        if (!shiftM[s.UserID]) shiftM[s.UserID] = [];
        shiftM[s.UserID].push({ start: s.ShiftStart, end: s.ShiftEnd, hours: s.Hours });
      }
      setShiftMap(shiftM);
    } catch {
      message.error('Failed to load schedule.');
      setShiftMap({});
    } finally {
      setLoading(false);
    }
  }, [teamIds, jobIds, date]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  // ── Absences covering the day ────────────────────────────────────────────
  useEffect(() => {
    if (!canSeeAbsences || !showAbsences || !teamIds.length || !date) { setAbsenceMap({}); return; }
    api.get('/api/absences/for-day', { params: { team_ids: teamIds.join(','), date: date.format('YYYY-MM-DD') } })
      .then((r) => {
        const m = {};
        for (const a of r.data || []) (m[a.UserID] = m[a.UserID] || []).push(a);
        setAbsenceMap(m);
      })
      .catch(() => setAbsenceMap({}));
  }, [canSeeAbsences, showAbsences, teamIds, date]);

  // The absence covering a 15-minute slot (overlapping it at all), if any.
  const absenceAt = (agentId, slot) => {
    const list = absenceMap[agentId];
    if (!list) return null;
    const a0 = slotMins(slot); const a1 = a0 + 15;
    return list.find((a) => a.Spans.some(([f, t]) => a0 < t && a1 > f)) || null;
  };
  const absenceTip = (a) => `Absent: ${a.Reason} · ${a.AbsencePeriod === 'Absent Part of Day' ? 'Partial' : 'Full'} Day`
    + ` · ${a.StartTime}–${a.EndTime} · ${a.Status}`;

  // ── Actual data ──────────────────────────────────────────────────────────
  const loadActual = useCallback(async () => {
    if (!teamIds.length || !date) { setActualSlotMap({}); return; }
    setActualLoading(true);
    try {
      const params = { team_ids: teamIds.join(','), date: date.format('YYYY-MM-DD') };
      if (jobIds.length) params.job_ids = jobIds.join(',');
      const res = await api.get('/api/schedule/actual', { params });
      // One entry per agent and slot: its first break / lunch state, and the
      // seconds off the queue otherwise (offqSecs: QA Coaching and Non-ACD,
      // which can share the slot). A slot with no break shows the larger of
      // QA Coaching and Non-ACD.
      const map = {};
      for (const r of res.data.records || []) {
        const key = `${r.UserID}-${r.SlotTime}`;
        const e = map[key] || (map[key] = { SlotType: null, state: null, duration: 0, offqSecs: 0, offq: {} });
        const type = classifyState(r.State);
        if (type === 'Break' || type === 'Lunch') {
          if (!e.SlotType) Object.assign(e, { SlotType: type, state: r.State, duration: r.Duration });
        } else {
          e.offqSecs += r.Duration || 0;
          e.offq[type] = (e.offq[type] || 0) + (r.Duration || 0);
        }
      }
      for (const e of Object.values(map)) {
        if (e.SlotType) continue;
        const [type, secs] = Object.entries(e.offq).sort((a, b) => b[1] - a[1])[0];
        Object.assign(e, { SlotType: type, state: type, duration: secs });
      }
      setActualSlotMap(map);
    } catch {
      message.error('Failed to load actual data.');
    } finally {
      setActualLoading(false);
    }
  }, [teamIds, jobIds, date]);

  // Load actual whenever switching to that view or when deps change while in that view
  useEffect(() => {
    if (view === 'actual') loadActual();
  }, [view, loadActual]);

  // ── Outside-click dismisses plan popover ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest?.('.sched-cell') && !e.target.closest?.('.ant-popover')) {
        setOpenCell(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Plan edit handlers ───────────────────────────────────────────────────
  const handleSetSlot = async (agent, slotTime, slotType) => {
    setOpenCell(null);
    const key  = `${agent.ID}-${slotTime}`;
    const prev = slotMap[key];
    setSaving(p => new Set(p).add(key));
    setSlotMap(m => ({ ...m, [key]: { ID: null, SlotType: slotType } }));
    try {
      const res = await api.put('/api/schedule/slot', {
        team_id: agent.TeamID,
        date: date.format('YYYY-MM-DD'),
        agent_user_id: agent.ID,
        slot_time: slotTime,
        slot_type: slotType,
      });
      setSlotMap(m => ({ ...m, [key]: { ID: res.data.id, SlotType: slotType } }));
    } catch {
      message.error('Failed to save.');
      setSlotMap(m => { const n = { ...m }; if (prev) n[key] = prev; else delete n[key]; return n; });
    } finally {
      setSaving(p => { const s = new Set(p); s.delete(key); return s; });
    }
  };

  const handleClearSlot = async (agent, slotTime) => {
    setOpenCell(null);
    const key  = `${agent.ID}-${slotTime}`;
    const prev = slotMap[key];
    setSaving(p => new Set(p).add(key));
    setSlotMap(m => { const n = { ...m }; delete n[key]; return n; });
    try {
      await api.delete('/api/schedule/slot', {
        data: { team_id: agent.TeamID, date: date.format('YYYY-MM-DD'), agent_user_id: agent.ID, slot_time: slotTime },
      });
    } catch {
      message.error('Failed to remove.');
      if (prev) setSlotMap(m => ({ ...m, [key]: prev }));
    } finally {
      setSaving(p => { const s = new Set(p); s.delete(key); return s; });
    }
  };

  const handleCellClick = (agentId, slotTime) => {
    if (!canEdit || view !== 'plan') return;
    setOpenCell(prev =>
      prev?.agentId === agentId && prev?.slotTime === slotTime ? null : { agentId, slotTime }
    );
  };

  // ── Copy handlers ────────────────────────────────────────────────────────
  const openCopyModal = () => {
    setCopyDate(date ? date.add(1, 'day') : dayjs().add(1, 'day'));
    setCopyNonAcd(false);
    setCopyOpen(true);
  };

  const handleCopy = async () => {
    if (!copyDate) return;
    setCopying(true);
    try {
      const res = await api.post('/api/schedule/copy', {
        team_ids: teamIds,
        from_date: date.format('YYYY-MM-DD'),
        to_date: copyDate.format('YYYY-MM-DD'),
        copy_non_acd: copyNonAcd,
      });
      message.success(`Copied ${res.data.copied} slot${res.data.copied !== 1 ? 's' : ''} to ${copyDate.format('MMM D, YYYY')}.`);
      setCopyOpen(false);
    } catch (err) {
      message.error(err.response?.data?.error || 'Copy failed.');
    } finally {
      setCopying(false);
    }
  };

  // ── Row renderer (shared by Plan + Actual) ───────────────────────────────
  const renderAgentRow = (agent, ri) => {
    const rowBg  = ri % 2 ? '#fafafa' : '#fff';
    const isPlan = view === 'plan';
    // The day's shift times (each piece of a split shift) and scheduled
    // (Paycor paid) hours, e.g. "7am–3:30pm 8 hrs"; blank when not scheduled.
    const dayShifts = shiftMap[agent.ID] || [];
    const dayHours  = dayShifts.length && dayShifts.every((s) => s.hours != null)
      ? Math.round(dayShifts.reduce((t, s) => t + s.hours, 0) * 100) / 100 : null;
    const shortTime = (hm) => {
      const [h, m] = hm.split(':').map(Number);
      return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`;
    };
    const dayTimes = [...dayShifts].sort((a, b) => a.start.localeCompare(b.start))
      .map((s) => `${shortTime(s.start)}–${shortTime(s.end)}`).join(', ');
    const daySummary = [dayTimes, dayHours != null ? `${dayHours} hrs` : ''].filter(Boolean).join(' ');

    return (
      <tr key={agent.ID}>
        <td style={agentCellStyle(rowBg)} title={daySummary ? `${agent.DisplayName} (${daySummary})` : agent.DisplayName}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
              {(canBoard && dayShifts.length) || canWfm ? (
                <Typography.Link style={{ fontSize: 12 }} onClick={() => setPersonFor(agent)}>{agent.DisplayName}</Typography.Link>
              ) : agent.DisplayName}
            </span>
            {daySummary && <span style={{ marginLeft: 'auto', opacity: 0.65, flexShrink: 0 }}>{daySummary}</span>}
          </div>
        </td>
        <td style={jobCellStyle(rowBg)} title={agent.JobName}>
          {agent.JobName || ''}
        </td>
        {timeSlots.map(slot => {
          const key        = `${agent.ID}-${slot}`;
          const planData   = slotMap[key];
          const actualData = actualSlotMap[key];
          const slotData   = isPlan ? planData : actualData;
          const isActive   = isPlan && openCell?.agentId === agent.ID && openCell?.slotTime === slot;
          const isSaving   = isPlan && saving.has(key);
          const isHour     = slot.slice(3) === '00';
          const meta        = slotData ? (SLOT_META[slotData.SlotType] || SLOT_META.Other) : null;
          const agentShifts = shiftMap[agent.ID] || [];
          const inShift     = agentShifts.some(sh => slot >= sh.start && slot < sh.end);
          const absent      = absenceAt(agent.ID, slot);
          const cellBg      = absent ? absentBg(inShift ? SHIFT_BG : rowBg) : (inShift ? SHIFT_BG : rowBg);

          // ── Actual view: split-strip cell (Plan on top, Actual on bottom) ──
          if (!isPlan) {
            const isShortBreakLunch = actualData &&
              (actualData.SlotType === 'Break' || actualData.SlotType === 'Lunch') &&
              (actualData.duration ?? 0) < 60;
            const effectiveActual = (hideShortBreakLunch && isShortBreakLunch) ? null : actualData;
            const planMeta   = planData       ? (SLOT_META[planData.SlotType]       || SLOT_META.Other) : null;
            const actualMeta = effectiveActual ? (SLOT_META[effectiveActual.SlotType] || SLOT_META.Other) : null;
            const hasSomething = planMeta || actualMeta;

            // Width of actual bar: 15 min (900 s) = 100% of cell; shorter durations fill proportionally.
            const actualSecs     = effectiveActual?.duration ?? 0;
            const actualWidthPct = actualMeta && actualSecs > 0
              ? Math.round(Math.min(actualSecs, 900) / 900 * 100)
              : 0;
            const actualMins     = Math.round(actualSecs / 60);

            const stripCell = hasSomething ? (
              <div style={{
                height: 28, display: 'flex', flexDirection: 'column',
                borderRadius: 4, overflow: 'hidden', cursor: 'default',
              }}>
                {/* top = Plan (fixed 13 px, full width) */}
                <div style={{
                  height: 13, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: planMeta?.bg || '#e8e8e8',
                  fontSize: 9, fontWeight: 700,
                  color: planMeta ? '#fff' : '#bfbfbf',
                }}>
                  {planMeta?.label || ''}
                </div>
                <div style={{ height: 1, flexShrink: 0, background: '#fff' }} />
                {/* bottom = Actual (width proportional to duration / 900 s) */}
                <div style={{ flex: 1, background: '#ddd', position: 'relative', overflow: 'hidden' }}>
                  {actualWidthPct > 0 && (
                    <div style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0,
                      width: `${actualWidthPct}%`, minWidth: 3,
                      background: actualMeta.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 8, fontWeight: 700, color: '#fff',
                      overflow: 'hidden',
                    }}>
                      {actualMins > 0 && actualWidthPct >= 30 ? `${actualMins}m` : ''}
                    </div>
                  )}
                </div>
              </div>
            ) : <div style={{ height: 28 }} />;

            const tipLines = [];
            if (absent)     tipLines.push(absenceTip(absent));
            if (planData)   tipLines.push(`Plan: ${planData.SlotType}`);
            if (effectiveActual) tipLines.push(`Actual: ${effectiveActual.state}${effectiveActual.duration ? ` (${Math.round(effectiveActual.duration / 60)} min)` : ''}`);
            if (!planData && effectiveActual) tipLines.push('Plan: none');
            if (planData   && !actualData) tipLines.push('Actual: none recorded');

            return (
              <td key={slot} style={{
                padding: 2,
                borderBottom: '1px solid #f0f0f0',
                borderLeft: isHour ? '2px solid #bfbfbf' : '1px solid #f0f0f0',
                background: cellBg,
                width: SLOT_COL_W, minWidth: SLOT_COL_W,
              }}>
                {hasSomething || absent ? (
                  <Tooltip
                    title={<span style={{ whiteSpace: 'pre-line' }}>{tipLines.join('\n')}</span>}
                    mouseEnterDelay={0.1}
                  >
                    {stripCell}
                  </Tooltip>
                ) : stripCell}
              </td>
            );
          }

          // ── Plan view: full-height editable cell ──────────────────────────
          const cellDiv = (
            <div
              className="sched-cell"
              onClick={() => handleCellClick(agent.ID, slot)}
              style={{
                height: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isSaving ? '#d9d9d9' : isActive ? (meta?.bg || '#e6f4ff') : (meta?.bg || 'transparent'),
                borderRadius: 4,
                cursor: canEdit ? 'pointer' : 'default',
                fontSize: 11, fontWeight: 700,
                color: meta?.fg || (absent ? ABSENT_FG : 'transparent'),
                transition: 'background 0.1s',
                userSelect: 'none',
                outline: isActive ? '2px solid #1677ff' : 'none',
                outlineOffset: -1,
              }}
            >
              {isSaving ? '…' : (meta?.label || (absent ? (REASON_LETTER[absent.Reason] || 'A') : ''))}
            </div>
          );

          const planContent = slotData ? (
            <div className="sched-cell" style={{ minWidth: 130 }}>
              <div style={{ fontWeight: 600, color: meta?.bg, marginBottom: 8, fontSize: 13 }}>
                {slotData.SlotType} — {slot}
              </div>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {PLAN_TYPES.filter((t) => t !== slotData.SlotType).map((t) => (
                  <Button key={t} block size="small"
                    style={{ background: SLOT_META[t].bg, color: '#fff', border: 'none' }}
                    onClick={() => handleSetSlot(agent, slot, t)}
                  >
                    Switch to {t}
                  </Button>
                ))}
                <Button block size="small" danger onClick={() => handleClearSlot(agent, slot)}>Remove</Button>
              </Space>
            </div>
          ) : (
            <div className="sched-cell">
              <Space size={6} wrap style={{ maxWidth: 190 }}>
                {PLAN_TYPES.map((t) => (
                  <Button key={t} size="small" style={{ background: SLOT_META[t].bg, color: '#fff', border: 'none', fontWeight: 600 }}
                    onClick={() => handleSetSlot(agent, slot, t)}>{t}</Button>
                ))}
              </Space>
            </div>
          );

          return (
            <td key={slot} style={{
              padding: 2,
              borderBottom: '1px solid #f0f0f0',
              borderLeft: isHour ? '2px solid #bfbfbf' : '1px solid #f0f0f0',
              background: cellBg,
              width: SLOT_COL_W, minWidth: SLOT_COL_W,
            }}>
              {isActive && canEdit ? (
                <Popover open content={planContent} trigger={[]} destroyPopupOnHide
                  onOpenChange={open => !open && setOpenCell(null)}
                  getPopupContainer={() => containerRef.current || document.body}
                  arrow={false}
                >
                  {cellDiv}
                </Popover>
              ) : absent ? (
                <Tooltip title={absenceTip(absent) + (planData ? `\nPlan: ${planData.SlotType}` : '')}
                  overlayStyle={{ whiteSpace: 'pre-line' }} mouseEnterDelay={0.1}>
                  {cellDiv}
                </Tooltip>
              ) : cellDiv}
            </td>
          );
        })}
      </tr>
    );
  };

  // ── Filter: hide agents with no shift and no break/lunch entries ────────
  const visibleAgents = (() => {
    if (!excludeUnscheduled) return agents;
    const activeMap = view === 'plan' ? slotMap : actualSlotMap;
    const withEntries = new Set(Object.keys(activeMap).map((k) => parseInt(k, 10)));
    return agents.filter((a) => shiftMap[a.ID]?.length > 0 || withEntries.has(a.ID));
  })();

  // ── Grouped agents ───────────────────────────────────────────────────────
  const grouped = (() => {
    const map = {};
    for (const a of visibleAgents) {
      if (!map[a.TeamID]) map[a.TeamID] = { teamName: a.TeamName, agents: [] };
      map[a.TeamID].agents.push(a);
    }
    return Object.values(map);
  })();

  const totalCols = timeSlots.length + 2;

  // Under each team: per slot, Scheduled (on shift), Out (on shift and
  // absent), Break/Lunch (on shift, not absent, on one -- plan or actual,
  // with the view), and Available (the rest).
  const countRows = (teamAgents) => {
    // Shared with Campaign Stats Today > Staffing, so the two agree.
    // Shares of the 15 minutes, as Calls & Staff counts them: an absence
    // covering 5 minutes is 0.33; an actual break its seconds / 900; a
    // planned one 1 (the plan is whole blocks).
    const per = staffingPerSlot({
      agents: teamAgents, slots: timeSlots, shiftMap,
      absentFrac: (id, slot) => absenceShare(absenceMap[id], slot),
      breakFrac: (id, slot) => {
        const isBrk = (x) => x && (x.SlotType === 'Break' || x.SlotType === 'Lunch');
        if (view === 'plan') return isBrk(slotMap[`${id}-${slot}`]) ? 1 : 0;
        const a = actualSlotMap[`${id}-${slot}`];
        if (!isBrk(a)) return 0;
        // As the cells show it: under a minute is left out while that option is on.
        if (hideShortBreakLunch && (a.duration ?? 0) < 60) return 0;
        return Math.min((a.duration ?? 0) / 900, 1);
      },
      // Planned QA Coaching / Non-ACD; in the Actual view, the seconds in
      // either, under a minute left out.
      offqFrac: (id, slot) => {
        if (view === 'plan') return OFFQ_TYPES.includes(slotMap[`${id}-${slot}`]?.SlotType) ? 1 : 0;
        const secs = actualSlotMap[`${id}-${slot}`]?.offqSecs ?? 0;
        return secs < 60 ? 0 : Math.min(secs / 900, 1);
      },
    });
    const row = (label, key, color) => (
      <tr key={key}>
        <td colSpan={2} style={{ ...agentCellStyle('#fafafa'), fontWeight: 700, fontSize: 11 }}>{label}</td>
        {per.map((c, i) => (
          <td key={timeSlots[i]} style={{
            textAlign: 'center', fontSize: 11, fontWeight: 600, padding: '2px 0',
            color: c[key] ? color : '#d9d9d9', background: '#fafafa',
            borderBottom: '1px solid #f0f0f0',
            borderLeft: timeSlots[i].slice(3) === '00' ? '2px solid #bfbfbf' : '1px solid #f0f0f0',
          }}>
            {c[key] ? Number(c[key].toFixed(2)) : (key === 'avail' && c.sched ? 0 : '')}
          </td>
        ))}
      </tr>
    );
    return [
      row('Scheduled', 'sched', '#434343'),
      row('Out (absent notification)', 'out', ABSENT_FG),
      row('Break / Lunch', 'brk', '#d46b08'),
      row('QA Coaching / Non-ACD', 'offq', '#722ed1'),
      row('Available', 'avail', '#237804'),
    ];
  };
  const isLoading = view === 'plan' ? loading : actualLoading;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        {embedded ? <span /> : <Typography.Title level={4} style={{ margin: 0 }}>Schedule Break & Lunch</Typography.Title>}
        {date && (
          <Typography.Text type="secondary">{date.format('dddd, MMMM D, YYYY')}</Typography.Text>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <Space wrap>
          {!lockTeams && (
            <Select
              mode="multiple"
              maxTagCount="responsive"
              placeholder="Select teams"
              value={teamIds}
              onChange={v => { setTeamIds(v); setOpenCell(null); }}
              options={teamOptions.map(t => ({ label: t.Name, value: t.ID }))}
              style={{ minWidth: 220 }}
              allowClear
            />
          )}
          <Select
            mode="multiple"
            maxTagCount="responsive"
            placeholder="All jobs"
            value={jobIds}
            onChange={v => { setJobIds(v); setOpenCell(null); }}
            options={allJobs.filter(j => j.IsActive).map(j => ({ label: j.JobName, value: j.JobID }))}
            style={{ minWidth: 180 }}
            allowClear
          />
          <DatePicker
            value={date}
            onChange={v => { if (v) { setDate(v); setOpenCell(null); } }}
            allowClear={false}
          />
          <Button size="small" onClick={() => { setDate(dayjs()); setOpenCell(null); }}>Today</Button>
          <Space size={4}>
            <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
            <Select value={startHour} onChange={setStartHour} options={HOUR_OPTIONS} style={{ width: 110 }} size="small" />
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>to</span>
            <Select value={endHour} onChange={setEndHour} options={HOUR_OPTIONS.filter(o => o.value > startHour)} style={{ width: 110 }} size="small" />
          </Space>
          {canEdit && agents.length > 0 && view === 'plan' && (
            <Button size="small" icon={<CopyOutlined />} onClick={openCopyModal}>Copy to…</Button>
          )}
        </Space>

        <Space>
          {canSeeAbsences && !embedded && (
            <Checkbox checked={showAbsences} onChange={(e) => setShowAbsences(e.target.checked)}>
              <Typography.Text style={{ fontSize: 12 }}>Show absences</Typography.Text>
            </Checkbox>
          )}
          <Checkbox
            checked={excludeUnscheduled}
            onChange={(e) => setExcludeUnscheduled(e.target.checked)}
          >
            <Typography.Text style={{ fontSize: 12 }}>Exclude non-scheduled</Typography.Text>
          </Checkbox>
          {view === 'actual' && (
            <Checkbox
              checked={hideShortBreakLunch}
              onChange={(e) => setHideShortBreakLunch(e.target.checked)}
            >
              <Typography.Text style={{ fontSize: 12 }}>Hide break/lunch &lt; 1 min</Typography.Text>
            </Checkbox>
          )}
          <Segmented
            value={view}
            onChange={v => { setView(v); setOpenCell(null); }}
            options={[
              { label: 'Plan',   value: 'plan'   },
              { label: 'Actual', value: 'actual' },
            ]}
          />
        </Space>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : !teamIds.length ? (
        <Empty description="Select one or more teams to view the schedule" style={{ padding: 60 }} />
      ) : visibleAgents.length === 0 ? (
        <Empty description={agents.length === 0 ? 'No agents assigned to the selected teams' : 'No scheduled agents for this day'} style={{ padding: 60 }} />
      ) : (
        <div ref={containerRef} style={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 260px)',
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e8e8e8',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: EMPLOYEE_COL_W }} />
              <col style={{ width: JOB_COL_W }} />
              {timeSlots.map(s => <col key={s} style={{ width: SLOT_COL_W }} />)}
            </colgroup>
            <thead>
              <tr>
                <th style={agentHeaderStyle}>Employee</th>
                <th style={jobHeaderStyle}>Job</th>
                {timeSlots.map(slot => {
                  const isHour = slot.slice(3) === '00';
                  return (
                    <th key={slot} style={{
                      position: 'sticky', top: 0, zIndex: 2,
                      textAlign: 'center', fontSize: 10, padding: '5px 1px',
                      borderBottom: '2px solid #d9d9d9',
                      borderLeft: isHour ? '2px solid #bfbfbf' : '1px solid #f0f0f0',
                      fontWeight: isHour ? 700 : 400,
                      color: isHour ? '#434343' : '#8c8c8c',
                      background: '#f0f0f0',
                      userSelect: 'none', whiteSpace: 'nowrap',
                    }}>
                      {slotLabel(slot)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grouped.map(({ teamName, agents: teamAgents }) => (
                <React.Fragment key={teamName}>
                  <tr>
                    <td colSpan={totalCols} style={{
                      position: 'sticky', left: 0,
                      background: '#dce8ff',
                      padding: '5px 12px',
                      fontSize: 12, fontWeight: 700,
                      borderTop: '2px solid #adc8ff',
                      borderBottom: '1px solid #adc8ff',
                      color: '#1d39c4',
                      letterSpacing: 0.3,
                      zIndex: 2,
                    }}>
                      {teamName}
                    </td>
                  </tr>
                  {/* The team's counts first, under its name, so they read with
                      the team even after scrolling down a long list. */}
                  {canSeeAbsences && countRows(teamAgents)}
                  {teamAgents.map((agent, ri) => renderAgentRow(agent, ri))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <Space size={16} wrap>
          {Object.entries(SLOT_META).filter(([k]) => k !== 'Other').map(([type, m]) => (
            <Space key={type} size={4}>
              <div style={{ width: 14, height: 14, background: m.bg, borderRadius: 3 }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{type}</Typography.Text>
            </Space>
          ))}
          {canSeeAbsences && showAbsences && (
            <Space size={4}>
              <div style={{ width: 14, height: 14, background: absentBg('#fff'), borderRadius: 3, border: '1px solid #ffa39e' }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Absent (S Sick · O Outage · J Jury Duty · B Bereavement · X Other)
              </Typography.Text>
            </Space>
          )}
          {Object.keys(shiftMap).length > 0 && (
            <Space size={4}>
              <div style={{ width: 14, height: 14, background: SHIFT_BG, borderRadius: 3, border: '1px solid #93c5fd' }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Scheduled shift</Typography.Text>
            </Space>
          )}
          {view === 'plan' && canEdit && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Click any cell to assign or change</Typography.Text>
          )}
          {view === 'actual' && (
            <Space size={8}>
              {/* mini two-strip legend swatch */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, width: 18, height: 18, borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
                <div style={{ flex: 1, background: SLOT_META.Break.bg }} />
                <div style={{ flex: 1, background: '#e8e8e8' }} />
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Top strip = Plan &nbsp;|&nbsp; Bottom bar width = Actual duration (full width = 15 min) &nbsp;·&nbsp; Hover for details</Typography.Text>
            </Space>
          )}
        </Space>
      </div>

      <Modal maskClosable={false}
        title="Copy Schedule"
        open={copyOpen}
        onCancel={() => setCopyOpen(false)}
        onOk={handleCopy}
        okText="Copy"
        okButtonProps={{ disabled: !copyDate || copyDate.isSame(date, 'day'), loading: copying }}
        width={400}
      >
        <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 8 }}>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Copying from</Typography.Text>
            <div style={{ fontWeight: 600 }}>{date?.format('dddd, MMMM D, YYYY')}</div>
          </div>
          <div>
            <Typography.Text style={{ display: 'block', marginBottom: 6 }}>Copy to date</Typography.Text>
            <DatePicker
              value={copyDate}
              onChange={v => setCopyDate(v)}
              allowClear={false}
              disabledDate={d => d && d.isSame(date, 'day')}
              style={{ width: '100%' }}
            />
          </div>
          <Checkbox checked={copyNonAcd} onChange={(e) => setCopyNonAcd(e.target.checked)}>
            Copy planned Non-ACD
          </Checkbox>
          {copyDate && !copyDate.isSame(date, 'day') && (
            <Alert
              type="warning"
              showIcon
              message={`Breaks and lunches${copyNonAcd ? ' and Non-ACD' : ''} already planned for ${copyDate.format('MMM D, YYYY')} across the selected teams will be replaced. QA Coaching${copyNonAcd ? '' : ' and Non-ACD'} planned there stays, and is not copied.`}
            />
          )}
        </Space>
      </Modal>
      <SchedulePersonDrawer agent={personFor} date={date} shifts={personFor ? shiftMap[personFor.ID] : null}
        canCover={canBoard} canWfm={canWfm} onClose={() => setPersonFor(null)} />
    </>
  );
}
